import { inject, Injectable } from '@angular/core';
import { LenderService } from '@shared/service/lender/lender.service';
import { getIterationId, getPageIterationId, processPath } from '../utilities.service';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import {
  ADDITIONAL_BENEFICIAL_COUNT,
  OAO_MEMBERSHIP_TYPE,
  RESPONSIBLE_PARTIES,
  RESPONSIBLE_PARTIES_ISPARTY
} from '@workflow/ecp-shared/constant/constants';
import { ApplicationFillType, IncomeFrequency, OaoMembershipTypes } from '@workflow/ecp-shared/enum/enum';
import { ValueLabelItem } from '@workflow/class/enum-item.class';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowDictionaryService } from '@workflow/service/workflow-dictionary/workflow-dictionary.service';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { AlloyService } from '../alloy/alloy.service';
import { StatefulService } from '@app/service/stateful.service';
import { FieldService } from '@workflow/class/field.service';
import { POBOX_REGEX } from '@shared/constant/regex.const';

/**
 * Methods on this service get registered as 'logic functions' on LogicService
 */
@Injectable({
  providedIn: 'root'
})
export class VisibleFunctionsService {
  private readonly statefulService = inject(StatefulService);
  private readonly fieldService = inject(FieldService);

  constructor(
    public readonly dictionaryService: WorkflowDictionaryService,
    private readonly lenderService: LenderService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly workflowSvc: WorkflowService,
    private readonly alloyService: AlloyService
  ) {}

  /**
   * Returns true if testDate value is not yet over the requiredYears in the past
   * @param context logic fn field context
   * @param path source value storage path
   * @param requiredYears number of years required
   */
  public missingTimeCoverage = (context: LogicFnContext, path: string, requiredYears: number): boolean => {
    const MIN_YEAR = 1900;
    const N_DAYS = 31;
    const N_MONTHS = 12;

    const passDate = (date: string): boolean => {
      const startDate = new Date(date);
      return Date.now() >= startDate.setFullYear(startDate.getFullYear() + +requiredYears);
    };

    const iterationId = this.statefulService.arraySize(path, context.field.pageIterationId) - 1;
    const parts = this.statefulService.wrap(path, iterationId, context.field.pageIterationId).valueStringOnly.split('/');
    const month = +parts.shift();
    if (parts.length === 1) {
      const year = +parts.shift();
      if (month > 0 && month <= N_MONTHS && year >= MIN_YEAR && year <= new Date().getFullYear()) {
        return !passDate(`${month}/01/${year}`);
      }
    } else {
      const day = +parts.shift();
      const year = +parts.shift();
      if (month > 0 && month <= N_MONTHS && day > 0 && day <= N_DAYS && year >= MIN_YEAR && year <= new Date().getFullYear()) {
        return !passDate(`${month}/${day}/${year}`);
      }
    }
    return false;
  };

  public showOnFirstCard = (context: LogicFnContext): boolean => {
    const fieldOrCard = context.field || context.card;
    return fieldOrCard.iterationId === 0;
  };

  public dontShowOnFirstCard = (context: LogicFnContext): boolean => {
    return !this.showOnFirstCard(context);
  };

  /**
   * Check if card is last for field
   * @param context field context
   * @param path path on storage
   */
  public showOnLastCard = (context: LogicFnContext, pathValue: string): boolean => {
    const path = processPath(pathValue, context.field.pageIterationId);
    return context.field.iterationId === this.statefulService.arraySize(path) - 1;
  };

  /**
   * Returns true if referenced array is empty
   * @param context field context
   * @param path path on storage
   */
  public isEmptyArray = (context: LogicFnContext, path: string): boolean => {
    return !this.statefulService.arraySize(processPath(path, context));
  };

  /**
   * Returns true if referenced array is not empty
   * @param context field context
   * @param path path on storage
   */
  public isNotEmptyArray = (context: LogicFnContext, path: string): boolean => {
    return !this.isEmptyArray(context, path);
  };

  /**
   * Returns true if referenced array size is less than given number
   * @param context field context
   * @param path path on storage
   */
  public isArraySizeLessThan = (context: LogicFnContext, path: string, size: number): boolean => {
    return this.statefulService.arraySize(path) < size;
  };

  /**
   * Hide field if backing storage is falsy
   * Returns true only if there's value
   * @param context field context
   */
  public hideIfEmpty = (context: LogicFnContext): boolean => {
    return (
      context.field.config.storageId &&
      !!this.statefulService.wrap(context.field.config.storageId, context.field.iterationId, context.field.pageIterationId).value
    );
  };

  /**
   * Show Identity verification page.
   * @param context - LogicFnContext
   */
  public showIdentityVerificationPage = (
    context: LogicFnContext,
    lenderSettingStorageId: string,
    preciseIdCompletionFlagStorageId: string,
    IsPrimaryApplicantVisited: boolean
  ): boolean => {
    const oaolenderSettings = this.statefulService.wrap(lenderSettingStorageId).value;
    const PIDCompleted = this.statefulService.wrap(preciseIdCompletionFlagStorageId).value;
    const enabledAdminToolPID = this.isEnabledPreciseId();
    // if currentPage is identity then make page visible true
    const isCurrentPageIdentity = this.workflowSvc.currentPage && this.workflowSvc.currentPage.id === context.page.id;

    return oaolenderSettings && !PIDCompleted && enabledAdminToolPID && (!IsPrimaryApplicantVisited || isCurrentPageIdentity);
  };

  private isEnabledPreciseId = (): boolean => {
    const selectedMembershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.filter((t) => t.membershipID === selectedMembershipId);
    const enableMembershipPreciseId = adminMembership && adminMembership.length > 0 ? adminMembership[0].isEnabledPID : false;
    return adminPortalFeatures?.oaoFeatures?.enablePreciseId && enableMembershipPreciseId;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAlloyVerificationPage = (context: LogicFnContext): boolean => {
    // determine the mode based on the flowId
    let mode = '';
    if (context.page.flowId.toLowerCase().includes('ola')) {
      mode = 'ola';
    }
    if (context.page.flowId.toLowerCase().includes('oao')) {
      mode = 'api';
    }
    // determine the alloy state
    const alloyState = this.alloyService.isAlloyEnabled();
    if (alloyState.enabled && this.statefulService.wrap(`${mode}:journeyInfo/isAlloyCompleted`).value === true) {
      return false;
    }

    // we don't care about state here - handle that in the guard
    return alloyState.enabled;
  };

  public showCoApplicantPage = (context: LogicFnContext, coAppCount: number): boolean => {
    const selectedMembershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
    const adminPortalFeatures = this.adminPortalFeaturesService?.config;
    const adminMembership = adminPortalFeatures?.oaoConfiguration?.memberships?.filter((t) => t.membershipID === selectedMembershipId);

    if (adminMembership && adminMembership.length > 0) {
      return adminMembership[0]?.canAllowCoApplicant && adminMembership[0]?.allowedNumberOfCoApplicant >= coAppCount;
    }

    return false;
  };

  /**
   * Check the given storageId address contains the PO Box. If so return true, else false.
   * @param context - LogicFnContext
   * @param storageId - string
   */
  public checkPOBoxAddress = (context: LogicFnContext, storageId: string): boolean => {
    // get the pobox display message from dictionary service & replace with the msg holder

    // get the iterationid to get te address text.
    const iterationID = context.field.iterationId;
    const address = this.statefulService.wrap(storageId, iterationID, context.field.pageIterationId).value;

    if (address) {
      return POBOX_REGEX.test(String(address));
    }
    return false;
  };

  public hideActionButtonOnInstaTouch = (context: LogicFnContext, storagePath: string, applicationType = 'OAO'): boolean => {
    const isMembership = applicationType.toLowerCase() === 'oao';
    const isPrefillEnabled = isMembership ? this.visibleIsOAOPrefillEnabled(context) : this.isOlaPrefillEnabled(context);
    const pageId = getPageIterationId(context);
    const iterationId = getIterationId(context);
    const autoFillStatus = this.statefulService.wrap(`${storagePath}/autoFillStatus`, iterationId, pageId).value;
    const autoFillOption = this.statefulService.wrap(`${storagePath}/autoFillOption`, iterationId, pageId).valueString;
    const isAlloyPrefillInProgress = !!this.statefulService.wrap('mem:alloy-prefill-inprogress').value;
    const status = (autoFillOption?.toLowerCase() !== 'manual' && autoFillStatus !== true && isPrefillEnabled) || isAlloyPrefillInProgress;
    return status;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isYouthMembership = (context: LogicFnContext): boolean => {
    const value = this.statefulService.wrap('api:oaoMemberInformation/membership/isYouth').valueString;
    return value ? JSON.parse(value) : false;
  };

  public isNotYouthMembership = (context: LogicFnContext): boolean => {
    return !this.isYouthMembership(context);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public isBusinessMembership = (context: LogicFnContext): boolean => {
    const value = this.statefulService.wrap(OAO_MEMBERSHIP_TYPE).valueString;
    return value && value.toLowerCase() === OaoMembershipTypes.Business.toLowerCase();
  };

  public isNotBusinessMembership = (context: LogicFnContext): boolean => {
    return !this.isBusinessMembership(context);
  };

  /**
   * Show additional Beneficiary Card for Business.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public showAdditionalBeneficiaryCard = (context: LogicFnContext): boolean => {
    const responsibleCardSize = this.statefulService.arraySize(RESPONSIBLE_PARTIES);
    let selectedYesCount = 0;
    const maxCount = 4;
    for (let i = 0; i < responsibleCardSize; i++) {
      const temp = this.statefulService.wrap(RESPONSIBLE_PARTIES_ISPARTY, i);
      if (temp && temp.value === true) {
        selectedYesCount++;
      }
    }
    return maxCount - selectedYesCount > 0;
  };

  /**
   *  Show Beneficiary Card list for Business
   */
  public showAdditionalBeneficicaryCardList = (context: LogicFnContext): boolean => {
    const isAdditionalSelected = this.statefulService.wrap(ADDITIONAL_BENEFICIAL_COUNT).value;
    return isAdditionalSelected && this.showAdditionalBeneficiaryCard(context);
  };

  /**
   *  Show Instattouch  Card list for Business
   */
  public showInstatouchBusiness = (context: LogicFnContext, autfillOptionStorageID: string, autfillStatusStoragID: string): boolean => {
    const index = context && context.card && context.card.pageIterationId;
    const option = this.statefulService.wrap(autfillOptionStorageID, index).value;
    const status = this.statefulService.wrap(autfillStatusStoragID, index).value;
    return option === 'Manual' || status === true;
  };

  /**
   * @param context context for the current card
   * @param type 'primary' identified as reposnisble parties in 0 position
   * @returns boolean
   */
  public checkForActualEconsentCard = (context: LogicFnContext, type: 'primary' | 'additional') => {
    const index = context && context.card && context.card.pageIterationId;
    if (type === 'primary' && index === 0) {
      return true;
    }
    if (type !== 'primary' && index > 0) {
      return true;
    }
    return false;
  };

  /**
   *  Show/Hide Co-Applicant's Econsent Card for Business memberhip.
   * If Splash screen is enabled, hide econsent card for Primary applicant & show it for all co-applicants
   */
  public showBusinessEconsentCard = (context: LogicFnContext): boolean => {
    const pageIterationId = context && context.card && context.card.pageIterationId;
    const enableConsentScreen = this.adminPortalFeaturesService?.config?.consentManagement?.primaryAcknowledgement?.enableConsentScreen;
    return enableConsentScreen === true ? pageIterationId > 0 : true;
  };

  /**
   * show Issued By Text Field
   */
  public showAverageHoursWorked = (context: LogicFnContext, storageId: string): boolean => {
    try {
      const frequencyType = this.statefulService.wrap(storageId, context.field.iterationId, context.field.pageIterationId).valueStringOnly;
      return frequencyType && frequencyType.toString().toLowerCase() === IncomeFrequency.Hourly.toString().toLowerCase() ? true : false;
    } catch (e) {
      return false;
    }
  };

  /**
   *  Show PersonalInfoCard card based on autofill for Personal Membership
   */
  public showPersonalInfoCard = (context: LogicFnContext, autfillOptionStorageID: string, autfillStatusStoragID: string): boolean => {
    const autoFillStatus = this.statefulService.wrap(autfillStatusStoragID).value;
    const autoFillOption = this.statefulService.wrap(autfillOptionStorageID).value;
    const isAlloyPrefillInProgress = this.statefulService.wrap('mem:alloy-prefill-inprogress').value as boolean;

    return (
      !this.visibleIsOAOPrefillEnabled(context) ||
      autoFillOption === ApplicationFillType.Manual ||
      (autoFillStatus === true && !!ApplicationFillType[autoFillOption as string] && !isAlloyPrefillInProgress)
    );
  };

  /**
   * Show OLA PersonalInfoCard card based on autofill for Consumer Loan flows.
   * Mirrors showPersonalInfoCard but leverages unified OLA prefill enablement logic (isOlaPrefillEnabled).
   * Visible when:
   *  - Prefill not enabled for current context (journey disabled, co-app disallowed, etc) OR
   *  - User selected Manual option OR
   *  - User selected a prefill option (MobileNumber | Prefill | DXPrefill | Alloy) AND status flag is true
   */
  public showOlaPersonalInfoCard = (context: LogicFnContext, autfillOptionStorageID: string, autfillStatusStoragID: string): boolean => {
    const pageIterationId = context?.card?.pageIterationId ?? context?.field?.pageIterationId;
    const optionPath = processPath(autfillOptionStorageID, pageIterationId);
    const statusPath = processPath(autfillStatusStoragID, pageIterationId);

    const autoFillOption = this.statefulService.wrap(optionPath).value;
    const autoFillStatus = this.statefulService.wrap(statusPath).value === true;
    const prefillEnabled = this.isOlaPrefillEnabled(context);
    const isManual = autoFillOption === ApplicationFillType.Manual;
    const isAlloyPrefillInProgress = this.statefulService.wrap('mem:alloy-prefill-inprogress').value as boolean;

    return (
      isManual ||
      !prefillEnabled ||
      (autoFillStatus && autoFillOption === ApplicationFillType[autoFillOption as string] && !isAlloyPrefillInProgress)
    );
  };

  /**
   *  Show IdentificationTypeChildFields based on lender && Membership
   */
  public showIdentificationTypeChildFields = (
    context: LogicFnContext,
    fieldId: string,
    idType: string,
    identificationFieldsTypeConfig: string
  ): boolean => {
    if (!idType) {
      return false;
    }

    if (!identificationFieldsTypeConfig) {
      return false;
    }

    const objIdentificationTypeChildFields: any = JSON.parse(identificationFieldsTypeConfig);

    return objIdentificationTypeChildFields && objIdentificationTypeChildFields[fieldId]
      ? objIdentificationTypeChildFields[fieldId].isRequired
      : false;
  };

  /**
   *  Hide or Show Promotion code text box
   */
  public showOAOPromotionCode = (): boolean => {
    // api:oaoMemberInformation/isOaoUrlPromoCode - controls enable/disable of the field level
    // api:oaoMemberInformation/promotionCode1 - this is the contents of the field.
    // mem:oaoMemberInformation/membership/isPromoCode is set from the membership call, based on maxPromoCodeDisplay > 0
    // The below approach is only transient, will be wiped one the user logs back in
    // This current isVisible evaluation is based on mem, so as soon as the angular app is refreshed, this will dissapear
    // 2 ways to fix this:
    // -  membership call has MaxPromoAllowed on a per product bases, which we could use if we can add that property to appInit
    //    and evalute it here.
    // - We could store te MaxPromoAllowed to the bundle, then grab from the bundle and evaludate it here
    const isPromoCodeEnabled = this.adminPortalFeaturesService?.config?.oaoFeatures?.allowPromoCode;
    const maxPromoCode = this.statefulService.wrap('api:oaoMemberInformation/membership/maxPromoCodeDisplay').value as number;
    return isPromoCodeEnabled && maxPromoCode > 0;
  };

  public visibleIsOAOPrefillEnabled = (context: LogicFnContext): boolean => {
    const lender = this.lenderService?.lenderData;
    const adminConfig = this.adminPortalFeaturesService?.config;
    const oaoFeatures = adminConfig?.oaoFeatures;

    if (lender?.enableAlloyPrefill) {
      const enabled = this.alloyService.isAlloyEnabled()?.enabled ?? false;
      if (!enabled) return false;

      // Suppress if Alloy journey already completed
      const alloyCompleted = this.statefulService.wrap('api:journeyInfo/isAlloyCompleted').value === true;
      if (alloyCompleted) return false;

      // Resolve journey for the selected membership to validate journey-level flags
      const membershipId = this.statefulService.wrap('api:oaoMemberInformation/membership/membershipID').value;
      const membership = adminConfig?.oaoConfiguration?.memberships?.find((m) => m.membershipID === membershipId);
      const journeyId = membership?.alloyJourneyId;
      const journey = journeyId ? lender?.alloySettings?.journeys?.find((j) => j.id === journeyId) : undefined;
      if (journey && journey.enableAlloyPrefill === false) {
        return false;
      }
      const currentPage = this.workflowSvc.currentPage;
      const pageIdStr = (currentPage?.config?.id || currentPage?.id || '').toLowerCase();
      const isCoApplicant = pageIdStr.includes('co-applicant') && !pageIdStr.includes('additional-co-app');
      const isAdditionalCoApplicant = pageIdStr.includes('additional-co-app');
      const isPrimary = !(isCoApplicant || isAdditionalCoApplicant);
      if (!isPrimary && journey && journey.allowCoApplicants === false) {
        return false;
      }
      return enabled;
    }

    return oaoFeatures?.enableInstatouch ?? false;
  };

  /**
   * showOaoAutofillApplicationCard
   * Keeps the autofill application card visible until BOTH conditions are met:
   *  - autoFillStatus is truthy (server/bundle indicates completion) AND
   *  - inprogress flag has been cleared (we explicitly ended our client flow)
   * Logic: visible while option != Manual AND visibleIsOAOPrefillEnabled AND (!status || inprogress)
   * This avoids premature hiding when the bundle sets autoFillStatus early.
   */
  public showOaoAutofillApplicationCard = (context: LogicFnContext): boolean => {
    try {
      if (!this.visibleIsOAOPrefillEnabled(context)) return false;
      // Derive applicant role from page id
      const pageIdStr = context.card.id.toLowerCase();
      const isAdditional = pageIdStr.includes('additional-co-app');
      const isCo = !isAdditional && pageIdStr.includes('co-applicant');
      let applicantKey: string;
      if (isAdditional) {
        applicantKey = 'additionalCoApplicant';
      } else if (isCo) {
        applicantKey = 'coApplicant';
      } else {
        applicantKey = 'primaryApplicant';
      }

      const base = `api:oaoMemberInformation/${applicantKey}`;
      const optionPath = `${base}/autoFillOption`;
      const statusPath = `${base}/autoFillStatus`;
      const option = this.statefulService.wrap(optionPath).valueStringOnly;
      const status = this.statefulService.wrap(statusPath).value as boolean;
      const inprogress =
        this.statefulService.wrap('mem:alloy-prefill-inprogress').value ?? this.statefulService.wrap('mem:oao-prefill-inprogress').value;
      if (option === ApplicationFillType.Manual) return false;
      const visible = !status || inprogress === true;

      return visible;
    } catch (e) {
      return false;
    }
  };

  /**
   * showOlaAutofillApplicationCard
   * Mirrors showOaoAutofillApplicationCard but for OLA (consumer loan) flows.
   * Keeps the autofill application card visible until BOTH:
   *  - autoFillStatus is true AND
   *  - inprogress sentinel cleared (mem:ola-prefill-inprogress OR mem:alloy-prefill-inprogress)
   * Visible while option != Manual AND isOlaPrefillEnabled AND (!status || inprogress)
   */
  public showOlaAutofillApplicationCard = (context: LogicFnContext): boolean => {
    const pageIdStr = context.card.id.toLowerCase();
    const isCoBorrower = pageIdStr.includes('co-applicant') || pageIdStr.includes('co-borrower');
    const isAdditional = pageIdStr.includes('additional-co-app');
    let applicantKey: string;
    if (isAdditional) {
      applicantKey = 'coBorrowers/1/borrowerInfo';
    } else if (isCoBorrower) {
      applicantKey = 'coBorrowers/#/borrowerInfo';
    } else {
      applicantKey = 'borrowerInfo';
    }

    // For dynamic co-borrower pages the JSON card id uses '#'; LogicFnContext will have pageIterationId we can substitute.
    const pageIterationId = context?.card?.pageIterationId;
    let base: string;
    if (applicantKey.includes('#') && pageIterationId != null) {
      base = `ola:${applicantKey.replace('#', String(pageIterationId))}`;
    } else {
      base = `ola:${applicantKey}`;
    }
    const optionPath = `${base}/autoFillOption`;
    const statusPath = `${base}/autoFillStatus`;

    return !this.showOlaPersonalInfoCard(context, optionPath, statusPath);
  };

  /**
   * Unified prefill visibility logic (Instatouch or Alloy) for OLA (consumer loan) flows.
   * Rules mirror OAO version:
   * 1. lender.enablePrefill must be true.
   * 2. If lender.enableAlloyPrefill === true use alloy enablement check.
   * 3. Else fall back to adminPortalFeatures.olaFeatures.canShowInstatouch flag.
   */
  public isOlaPrefillEnabled = (context: LogicFnContext): boolean => {
    const lender = this.lenderService?.lenderData;
    const adminConfig = this.adminPortalFeaturesService?.config;
    const olaFeatures = adminConfig?.olaFeatures;

    if (lender?.enableAlloyPrefill) {
      const enabled = this.alloyService.isAlloyEnabled()?.enabled ?? false;
      if (!enabled) return false;

      // Suppress if Alloy journey already completed for OLA flow
      const alloyCompleted = this.statefulService.wrap('ola:journeyInfo/isAlloyCompleted').value === true;
      if (alloyCompleted) return false;

      const selectedLoanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
      const selectedLoanProduct = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
      const loanGroupObj = adminConfig?.olaConfiguration?.consumerLoanGroups?.find((g) => g.loanGroup === selectedLoanGroup);
      const loanProductObj = loanGroupObj?.loanProducts?.find((p) => p.productName === selectedLoanProduct);
      const journeyId = loanProductObj?.alloyJourneyId;
      const journey = journeyId ? lender?.alloySettings?.journeys?.find((j) => j.id === journeyId) : undefined;
      if (journey && journey.enableAlloyPrefill === false) {
        return false;
      }
      const currentPage = this.workflowSvc.currentPage;
      const pageIdStr = (currentPage?.config?.id || currentPage?.id || '').toLowerCase();
      const isCoApplicant = pageIdStr.includes('co-applicant');
      if (isCoApplicant && journey && journey.allowCoApplicants === false) {
        return false;
      }
      return enabled;
    }

    return olaFeatures?.canShowInstatouch ?? false;
  };

  public enableEnhancedProductSelection = (): boolean => {
    return this.lenderService.lenderData.enableEnhancedProductSelection;
  };

  // TODO: is there a way in workflow to set visible to ["enableEnhancedProductSelection", false] to avoid duplicate code like this?
  public disableEnhancedProductSelection = (): boolean => {
    return !this.enableEnhancedProductSelection();
  };

  // Used to hide back button if previous page is membership or loan selection
  // We do not want the user to change their product selection after they have already selected one
  public previousPageIsProductSelection = (context: LogicFnContext): boolean => {
    return context?.page?.previousPage?.id === 'oao-membership' || context?.page?.previousPage?.id === 'ola-consumer-loan-type';
  };

  // Used to show or hide back button based on eligibility
  public previousPageIsEligibility = (context: LogicFnContext): boolean => {
    const isOlaSkipEligibility = this.statefulService.wrap('ola:skipEligibility').value as boolean;
    return (
      context?.page?.previousPage?.id === 'oao-eligibility' ||
      (context?.page?.previousPage?.id === 'ola-eligibility' && isOlaSkipEligibility)
    );
  };

  //  Hide Eligibility Questions Based on Admin Portal features for membership.
  public isOAOEligibilityQuestionsEnabled = (): boolean => {
    return this.adminPortalFeaturesService?.config?.oaoFeatures?.canShowEligibilityQuestions;
  };

  /**
   * Show Mobile phone number as default field for contact info
   * @param context logic fn field context
   * @param applicantType applicant type path
   * @param protocol storage prefix
   */
  public showOAODefaultContactInfo = (context: LogicFnContext, applicantType: string = 'primary', protocol: string = 'api'): void => {
    const pageId = getPageIterationId(context);
    const iterationID = getIterationId(context);
    const contactInfo = this.statefulService.wrap(`${protocol}:oaoMemberInformation/${applicantType}/phoneType`, iterationID, pageId).value;
    if (!contactInfo) {
      this.statefulService.wrap(`${protocol}:oaoMemberInformation/${applicantType}/phoneType`, iterationID, pageId).value = 'Mobile';
    }
  };

  /**
   * Show OAO Resident Monthyl Payment Field
   */
  public showOAOResidentMontlyPayment = (context: LogicFnContext, fieldId: string): boolean => {
    const field = this.fieldService.createField(fieldId, context.field?.flowId, context.field?.iterationId, context.field?.pageIterationId);
    if (field.config.payload) {
      if (field.config.payload['dropdownLists'] && (field.config.payload['dropdownLists'] as any).length > 0) {
        const dropdownValues = field.config.payload['dropdownLists']
          ? (JSON.parse(field.config.payload['dropdownLists'] as string) as ValueLabelItem[])
          : [];
        if (field.data.value && dropdownValues) {
          const items = dropdownValues.filter((f) => f.value === field.data.value);
          const canShow = items && items[0].label.toLowerCase() !== 'owns free and clear';
          if (!canShow) {
            context.field.data.value = null;
          }
          return canShow;
        }
        return true;
      }
    }
    return false;
  };
}
